import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Download, ExternalLink, AlertCircle } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { format } from 'date-fns';

interface BillDetailsModalProps {
  open: boolean;
  onClose: () => void;
  accountNumber: string;
  supplierName: string;
  customerName: string;
  month: string;
  status: 'available' | 'missing' | 'no_data';
  billId?: string;
  invoiceNumber?: string;
  amount?: number;
}

export function BillDetailsModal({
  open,
  onClose,
  accountNumber,
  supplierName,
  customerName,
  month,
  status,
  billId,
  invoiceNumber,
  amount
}: BillDetailsModalProps) {
  const monthLabel = format(new Date(month), 'MMMM yyyy');

  const handleRequestBill = () => {
    // TODO: Implement request bill action via edge function
    console.log('Request bill for:', { accountNumber, month });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Bill Details — {monthLabel}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Account Number</p>
              <p className="font-medium">{accountNumber}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Status</p>
              <div className="flex items-center gap-2">
                {status === 'available' && (
                  <span className="text-sm font-medium text-green-700">Available</span>
                )}
                {status === 'missing' && (
                  <span className="text-sm font-medium text-red-700 flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    Missing
                  </span>
                )}
                {status === 'no_data' && (
                  <span className="text-sm font-medium text-muted-foreground">No Data</span>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Supplier</p>
              <p className="font-medium">{supplierName}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Customer</p>
              <p className="font-medium">{customerName}</p>
            </div>
          </div>

          {status === 'available' && (
            <>
              {invoiceNumber && (
                <div>
                  <p className="text-sm text-muted-foreground">Invoice Number</p>
                  <p className="font-medium">{invoiceNumber}</p>
                </div>
              )}

              {amount && (
                <div>
                  <p className="text-sm text-muted-foreground">Amount</p>
                  <p className="text-xl font-bold">{formatCurrency(amount)}</p>
                </div>
              )}

              {billId && (
                <div className="flex gap-2 pt-4 border-t">
                  <Button variant="outline" className="flex-1" asChild>
                    <a href={`/bills/${billId}`} target="_blank">
                      <ExternalLink className="h-4 w-4 mr-2" />
                      View Bill
                    </a>
                  </Button>
                  <Button variant="outline" className="flex-1">
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </Button>
                </div>
              )}
            </>
          )}

          {status === 'missing' && (
            <div className="pt-4 border-t">
              <Button onClick={handleRequestBill} className="w-full">
                Request Bill
              </Button>
              <p className="text-xs text-muted-foreground text-center mt-2">
                This will create a collection job for the missing bill
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
